import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

interface AudioSpectrumVisualizerProps {
  audioElement: HTMLAudioElement | null;
  width?: number;
  height?: number;
  barCount?: number;
  barColor?: string;
  style?: 'bars' | 'wave' | 'circular';
}

const AudioSpectrumVisualizer: React.FC<AudioSpectrumVisualizerProps> = ({
  audioElement,
  width = 800,
  height = 200,
  barCount = 64,
  barColor = '#3B82F6',
  style = 'bars',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!audioElement || isInitialized) return;

    try {
      // Create audio context and analyser
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const analyser = audioContext.createAnalyser();

      // Configure analyser
      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      // Connect audio element to analyser
      const source = audioContext.createMediaElementSource(audioElement);
      source.connect(analyser);
      analyser.connect(audioContext.destination);

      analyserRef.current = analyser;
      dataArrayRef.current = dataArray;
      setIsInitialized(true);
    } catch (error) {
      console.error('Failed to initialize audio analyser:', error);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [audioElement, isInitialized]);

  useEffect(() => {
    if (!isInitialized || !analyserRef.current || !dataArrayRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;

    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);

      analyser.getByteFrequencyData(dataArray);

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      if (style === 'bars') {
        drawBars(ctx, dataArray);
      } else if (style === 'wave') {
        drawWave(ctx, dataArray);
      } else if (style === 'circular') {
        drawCircular(ctx, dataArray);
      }
    };

    const drawBars = (ctx: CanvasRenderingContext2D, dataArray: Uint8Array) => {
      const barWidth = width / barCount;
      const step = Math.floor(dataArray.length / barCount);

      for (let i = 0; i < barCount; i++) {
        const index = i * step;
        const value = dataArray[index];
        const barHeight = (value / 255) * height;

        const x = i * barWidth;
        const y = height - barHeight;

        // Create gradient
        const gradient = ctx.createLinearGradient(x, y, x, height);
        gradient.addColorStop(0, barColor);
        gradient.addColorStop(1, barColor + '40');

        ctx.fillStyle = gradient;
        ctx.fillRect(x, y, barWidth - 2, barHeight);

        // Add glow effect
        ctx.shadowBlur = 10;
        ctx.shadowColor = barColor;
        ctx.fillRect(x, y, barWidth - 2, barHeight);
        ctx.shadowBlur = 0;
      }
    };

    const drawWave = (ctx: CanvasRenderingContext2D, dataArray: Uint8Array) => {
      ctx.lineWidth = 2;
      ctx.strokeStyle = barColor;
      ctx.beginPath();

      const sliceWidth = width / barCount;
      let x = 0;

      for (let i = 0; i < barCount; i++) {
        const index = Math.floor(i * dataArray.length / barCount);
        const value = dataArray[index];
        const y = (value / 255) * height;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }

        x += sliceWidth;
      }

      ctx.stroke();

      // Fill under the wave
      ctx.lineTo(width, height);
      ctx.lineTo(0, height);
      ctx.closePath();

      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, barColor + '60');
      gradient.addColorStop(1, barColor + '10');
      ctx.fillStyle = gradient;
      ctx.fill();
    };

    const drawCircular = (ctx: CanvasRenderingContext2D, dataArray: Uint8Array) => {
      const centerX = width / 2;
      const centerY = height / 2;
      const radius = Math.min(width, height) / 3;

      for (let i = 0; i < barCount; i++) {
        const index = Math.floor(i * dataArray.length / barCount);
        const value = dataArray[index];
        const barLength = (value / 255) * radius;

        const angle = (i / barCount) * Math.PI * 2;
        const x1 = centerX + Math.cos(angle) * radius;
        const y1 = centerY + Math.sin(angle) * radius;
        const x2 = centerX + Math.cos(angle) * (radius + barLength);
        const y2 = centerY + Math.sin(angle) * (radius + barLength);

        const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
        gradient.addColorStop(0, barColor + '80');
        gradient.addColorStop(1, barColor);

        ctx.strokeStyle = gradient;
        ctx.lineWidth = (width / barCount) * 0.8;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }

      // Draw center circle
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius - 10, 0, Math.PI * 2);
      ctx.fillStyle = barColor + '20';
      ctx.fill();
    };

    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isInitialized, width, height, barCount, barColor, style]);

  return (
    <motion.div
      className="relative"
      style={{ width, height }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="rounded-lg"
      />

      {!isInitialized && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-gray-500 dark:text-gray-400 text-sm">
            Initializing visualizer...
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default AudioSpectrumVisualizer;
